/**
 * A thin wrapper over Office.js for the Outlook add-in (ADR 0033). Office.js is loaded from Microsoft's CDN by a script
 * tag added at run time (no npm package); only the few members the add-in uses are typed here.
 */

export const OFFICE_JS_URL = 'https://appsforoffice.microsoft.com/lib/1/hosted/office.js';

interface AsyncResult<T> {
  status: string;
  value: T;
  error?: { message?: string };
}
type Callback<T> = (r: AsyncResult<T>) => void;
interface Recipient {
  displayName?: string;
  emailAddress?: string;
}
interface Recipients {
  getAsync(cb: Callback<Recipient[]>): void;
}
interface MeetingItem {
  itemId?: string;
  getItemIdAsync?: (cb: Callback<string>) => void;
  subject: string | { getAsync(cb: Callback<string>): void };
  start: Date | { getAsync(cb: Callback<Date>): void };
  end: Date | { getAsync(cb: Callback<Date>): void };
  requiredAttendees: Recipient[] | Recipients;
  optionalAttendees: Recipient[] | Recipients;
}
interface DialogHandle {
  addEventHandler(type: unknown, handler: (arg: { message?: string; error?: number }) => void): void;
  close(): void;
}
export interface OfficeApi {
  onReady(cb?: (info: { host?: unknown }) => void): Promise<{ host?: unknown }>;
  EventType: { DialogMessageReceived: unknown; DialogEventReceived: unknown };
  AsyncResultStatus: { Succeeded: unknown };
  context: {
    mailbox?: { item?: MeetingItem | null };
    roamingSettings?: { get(k: string): unknown; set(k: string, v: unknown): void; remove(k: string): void; saveAsync(cb?: () => void): void };
    ui: {
      displayDialogAsync(url: string, opts: { height: number; width: number; displayInIframe?: boolean }, cb: Callback<DialogHandle>): void;
      messageParent(message: string): void;
    };
  };
}

export function getOffice(): OfficeApi | null {
  return (window as unknown as { Office?: OfficeApi }).Office ?? null;
}

let loading: Promise<OfficeApi | null> | null = null;

/** Adds the Office.js script tag once and resolves when Office is ready. Null outside Outlook or when the CDN is unreachable. */
export function loadOffice(timeoutMs = 8000): Promise<OfficeApi | null> {
  const existing = getOffice();
  if (existing) return existing.onReady().then(() => existing);
  loading ??= new Promise<OfficeApi | null>((resolve) => {
    const done = (): void => {
      const o = getOffice();
      if (!o) return resolve(null);
      void o.onReady().then(() => resolve(o));
    };
    const s = document.createElement('script');
    s.src = OFFICE_JS_URL;
    s.async = true;
    s.onload = done;
    s.onerror = () => resolve(null);
    document.head.appendChild(s);
    window.setTimeout(() => resolve(null), timeoutMs);
  });
  return loading;
}

const first = <T>(r: AsyncResult<T>, fail: () => Error): T => {
  if (String(r.status) !== 'succeeded') throw fail();
  return r.value;
};
const asyncValue = <T>(call: (cb: Callback<T>) => void): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    try {
      call((r) => {
        try {
          resolve(first(r, () => new Error(r.error?.message ?? 'office')));
        } catch (e) {
          reject(e instanceof Error ? e : new Error('office'));
        }
      });
    } catch (e) {
      reject(e instanceof Error ? e : new Error('office'));
    }
  });
/** A read-mode member is the value itself; a compose-mode member has getAsync. */
const read = <T>(v: T | { getAsync(cb: Callback<T>): void }): Promise<T> =>
  v !== null && typeof v === 'object' && 'getAsync' in (v as object) ? asyncValue((cb) => (v as { getAsync(cb: Callback<T>): void }).getAsync(cb)) : Promise.resolve(v as T);

export interface MeetingInfo {
  /** The Exchange item id. Only ever hashed (itemKey); never sent anywhere. */
  itemId: string;
  subject: string;
  start: string;
  end: string;
  attendees: { name: string; email: string }[];
}

export class MeetingReadError extends Error {
  constructor(readonly reason: 'no_item' | 'unsaved' | 'failed') {
    super(reason);
  }
}

/** Reads the open appointment (read or compose mode). */
export async function readMeeting(office: OfficeApi): Promise<MeetingInfo> {
  const item = office.context.mailbox?.item;
  if (!item) throw new MeetingReadError('no_item');
  try {
    let itemId = item.itemId ?? '';
    if (!itemId && item.getItemIdAsync) itemId = await asyncValue<string>((cb) => item.getItemIdAsync?.(cb));
    if (!itemId) throw new MeetingReadError('unsaved');
    const [subject, start, end, required, optional] = await Promise.all([
      read<string>(item.subject),
      read<Date>(item.start),
      read<Date>(item.end),
      read<Recipient[]>(item.requiredAttendees as Recipient[] | { getAsync(cb: Callback<Recipient[]>): void }),
      read<Recipient[]>(item.optionalAttendees as Recipient[] | { getAsync(cb: Callback<Recipient[]>): void }),
    ]);
    const seen = new Set<string>();
    const attendees: MeetingInfo['attendees'] = [];
    for (const r of [...required, ...optional]) {
      const email = (r.emailAddress ?? '').trim().toLowerCase();
      if (!email.includes('@') || seen.has(email)) continue;
      seen.add(email);
      attendees.push({ name: (r.displayName ?? '').trim(), email });
    }
    return { itemId, subject, start: new Date(start).toISOString(), end: new Date(end).toISOString(), attendees };
  } catch (e) {
    if (e instanceof MeetingReadError) throw e;
    throw new MeetingReadError('failed');
  }
}

/** SHA-256 hex of the item id, so the id itself is never stored (ADR 0033). */
export async function itemKeyOf(itemId: string): Promise<string> {
  const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(itemId));
  return Array.from(new Uint8Array(bytes), (b) => b.toString(16).padStart(2, '0')).join('');
}

// --- Token storage: the add-in's roaming settings (never in the meeting). Outside Outlook the browser's local storage is used. ---

const TOKEN_KEY = 'cloudsign.outlookToken';

export function loadToken(office: OfficeApi | null): string | null {
  const v = office?.context.roamingSettings ? office.context.roamingSettings.get(TOKEN_KEY) : window.localStorage.getItem(TOKEN_KEY);
  return typeof v === 'string' && v !== '' ? v : null;
}

export function saveToken(office: OfficeApi | null, token: string | null): void {
  const rs = office?.context.roamingSettings;
  if (rs) {
    if (token) rs.set(TOKEN_KEY, token);
    else rs.remove(TOKEN_KEY);
    rs.saveAsync();
    return;
  }
  if (token) window.localStorage.setItem(TOKEN_KEY, token);
  else window.localStorage.removeItem(TOKEN_KEY);
}

/** Opens the connect page in an Office dialog and resolves with the token the page sends back. */
export function connectDialog(office: OfficeApi, url: string): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    office.context.ui.displayDialogAsync(url, { height: 60, width: 30 }, (r) => {
      if (String(r.status) !== 'succeeded') return reject(new Error('dialog'));
      const dialog = r.value;
      dialog.addEventHandler(office.EventType.DialogMessageReceived, (arg) => {
        dialog.close();
        if (arg.message) resolve(arg.message);
        else reject(new Error('empty'));
      });
      dialog.addEventHandler(office.EventType.DialogEventReceived, () => reject(new Error('closed')));
    });
  });
}
